// Authentication types

export enum UserRole {
  USER = 'USER',
  ADMIN = 'ADMIN'
}

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  role: UserRole;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  role: UserRole;
}

export interface UpdateUserRequest {
  firstName: string;
  lastName: string;
  username: string;
  password?: string;
  role: UserRole;
  active: boolean;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

// ============================================================
// DevHub domain types
// ============================================================

export interface Client {
  id: number;
  name: string;
  contactEmail?: string | null;
  /** Postfach für den Rechnungsversand; leer = contactEmail. */
  billingEmail?: string | null;
  contactPhone?: string | null;
  notes?: string | null;
  contactPerson?: string | null;
  addressStreet?: string | null;
  addressZip?: string | null;
  addressCity?: string | null;
  addressCountry?: string | null;
  vatId?: string | null;
  commercialRegister?: string | null;
  bankAccountHolder?: string | null;
  bankName?: string | null;
  bankIban?: string | null;
  bankBic?: string | null;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateClientRequest {
  name: string;
  contactEmail?: string | null;
  billingEmail?: string | null;
  contactPhone?: string | null;
  notes?: string | null;
  contactPerson?: string | null;
  addressStreet?: string | null;
  addressZip?: string | null;
  addressCity?: string | null;
  addressCountry?: string | null;
  vatId?: string | null;
  commercialRegister?: string | null;
  bankAccountHolder?: string | null;
  bankName?: string | null;
  bankIban?: string | null;
  bankBic?: string | null;
}

export interface UpdateClientRequest extends CreateClientRequest {
  active: boolean;
}

export enum ProjectStatus {
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  COMPLETED = 'COMPLETED',
  ARCHIVED = 'ARCHIVED',
}

export interface Project {
  id: number;
  clientId: number;
  clientName?: string;
  name: string;
  description?: string | null;
  status: ProjectStatus;
  domain?: string | null;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateProjectRequest {
  clientId: number;
  name: string;
  description?: string | null;
  status: ProjectStatus;
  domain?: string | null;
}

export interface UpdateProjectRequest extends CreateProjectRequest {
  active: boolean;
}

export interface Repository {
  id: number;
  projectId: number;
  projectName?: string;
  clientName?: string;
  name: string;
  gitUrl: string;
  defaultBranch: string;
  description?: string | null;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateRepositoryRequest {
  projectId: number;
  name: string;
  gitUrl: string;
  defaultBranch: string;
  description?: string | null;
}

export interface UpdateRepositoryRequest extends CreateRepositoryRequest {
  active: boolean;
}

// ============================================================
// Tasks (Kanban)
// ============================================================

export enum TaskStatus {
  TODO = 'TODO',
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE',
}

export enum TaskPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export interface Task {
  id: number;
  projectId: number;
  title: string;
  description?: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  position: number;
  tags: string[];
  agentResult?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateTaskRequest {
  title: string;
  description?: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  tags?: string[];
}

export interface UpdateTaskRequest {
  title: string;
  description?: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  tags?: string[];
}

export interface MoveTaskRequest {
  status: TaskStatus;
  beforeId?: number | null;
  afterId?: number | null;
}

export interface UpdateTaskStatusRequest {
  status: TaskStatus;
  result?: string | null;
}

// ============================================================
// API Tokens (Agent-Schnittstelle, Admin-verwaltet)
// ============================================================

export interface ApiTokenResponse {
  id: number;
  name: string;
  prefix: string;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
  createdAt?: string | null;
}

export interface CreateApiTokenRequest {
  name: string;
}

export interface CreatedApiTokenResponse {
  token: ApiTokenResponse;
  plaintext: string;
}

// ============================================================
// Offers (Angebote)
// ============================================================

export enum OfferStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
}

export enum OfferSectionType {
  ONE_TIME = 'ONE_TIME',
  RECURRING = 'RECURRING',
}

export interface OfferPosition {
  id?: number;
  sectionId?: number;
  sortOrder: number;
  positionNumber: string;
  title: string;
  description?: string | null;
  /** Preis in Cent */
  unitPriceCents: number;
  quantity: number;
  unitLabel?: string | null;
}

export interface OfferSection {
  id?: number;
  offerId?: number;
  sortOrder: number;
  title: string;
  type: OfferSectionType;
  subtitle?: string | null;
  positions: OfferPosition[];
}

export interface Offer {
  id?: number;
  clientId: number;
  clientName?: string;
  projectId?: number | null;
  projectName?: string | null;

  number: string;
  title: string;
  contactPerson?: string | null;
  recipientAddress?: string | null;

  offerDate: string;             // ISO yyyy-MM-dd
  validUntil?: string | null;    // ISO yyyy-MM-dd

  status: OfferStatus;
  sentAt?: string | null;
  acceptedAt?: string | null;
  rejectedAt?: string | null;
  expiredAt?: string | null;

  introText?: string | null;
  notesHtml?: string | null;

  vatRatePercent: number;
  notesInternal?: string | null;
  active: boolean;

  sections: OfferSection[];

  createdAt?: string;
  updatedAt?: string;
}

export interface SendOfferRequest {
  to: string;
  cc?: string | null;
  subject?: string | null;
  message?: string | null;
  /** Nutzungsvereinbarung als zweites PDF anhängen */
  attachUsageAgreement: boolean;
  /** Vertrag zur Auftragsverarbeitung (Art. 28 DSGVO) anhängen */
  attachAvv: boolean;
}

export type OfferDocument = 'OFFER' | 'USAGE_AGREEMENT' | 'AVV';

/** Eintrag im Versandprotokoll eines Angebots */
export interface OfferEmail {
  id: number;
  offerId: number;
  offerNumber: string;
  sentAt: string;
  sentBy: string;
  recipient: string;
  cc?: string | null;
  subject: string;
  message?: string | null;
  offerPdfHash: string;
  hasUsageAgreement: boolean;
  hasAvv: boolean;
}

export interface CreateOfferRequest {
  clientId: number;
  projectId?: number | null;
  number: string;
  title: string;
  contactPerson?: string | null;
  recipientAddress?: string | null;
  offerDate: string;
  validUntil?: string | null;
  introText?: string | null;
  notesHtml?: string | null;
  vatRatePercent: number;
  notesInternal?: string | null;
  sections: OfferSection[];
}

export interface UpdateOfferRequest extends CreateOfferRequest {
  active: boolean;
}

export interface TransitionStatusRequest {
  status: OfferStatus;
}

// ============================================================
// Invoices (Rechnungen)
// ============================================================

export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  CANCELLED = 'CANCELLED',
}

export interface InvoicePosition {
  id?: number;
  sectionId?: number;
  sortOrder: number;
  positionNumber: string;
  title: string;
  description?: string | null;
  unitPriceCents: number;
  quantity: number;
  unitLabel?: string | null;
}

export interface InvoiceSection {
  id?: number;
  invoiceId?: number;
  sortOrder: number;
  title: string;
  subtitle?: string | null;
  positions: InvoicePosition[];
}

export interface Invoice {
  id?: number;
  clientId: number;
  clientName?: string;
  projectId?: number | null;
  projectName?: string | null;
  offerId?: number | null;
  offerNumber?: string | null;

  number: string;
  title: string;
  contactPerson?: string | null;
  recipientAddress?: string | null;

  invoiceDate: string;
  dueDate?: string | null;
  servicePeriodFrom?: string | null;
  servicePeriodTo?: string | null;

  status: InvoiceStatus;
  sentAt?: string | null;
  paidAt?: string | null;
  cancelledAt?: string | null;

  introText?: string | null;
  notesHtml?: string | null;
  paymentTerms?: string | null;

  vatRatePercent: number;
  notesInternal?: string | null;
  active: boolean;

  /** Festschreibung (GoBD): gesetzt, sobald die Rechnung versendet wurde. Danach unveränderbar. */
  lockedAt?: string | null;
  /** SHA-256 der archivierten PDF-Fassung. */
  documentHash?: string | null;
  cancelsInvoiceId?: number | null;
  cancelsInvoiceNumber?: string | null;
  cancelledByInvoiceId?: number | null;
  cancelledByInvoiceNumber?: string | null;
  /**
   * Welchem Vorhaben dieser Erlös zugerechnet wird, z. B. „Radsport-Events".
   *
   * Eine interne Auswertungsdimension — sie steht **nicht** auf dem Dokument und lässt sich
   * deshalb als einzige Angabe auch an einer festgeschriebenen Rechnung noch ändern.
   */
  businessArea?: string | null;
  /**
   * Verwendungszweck, den der Kunde angeben soll — leer heißt: die Rechnungsnummer.
   *
   * Gebraucht, wenn der Kunde ihn schon kennt (etwa aus einer Bestellbestätigung). Zwei
   * verschiedene wären einer zu viel; der Kontoabgleich sucht deshalb nach beidem.
   */
  paymentReference?: string | null;
  /**
   * Der Betrag war bei Ausstellung schon da (Vorkasse).
   *
   * Dokument und Mail danken dann, statt zur Zahlung aufzufordern. Steuert nur die Texte — ob
   * eine Rechnung bezahlt ist, entscheidet weiterhin der Kontoauszug.
   */
  alreadyPaid?: boolean;
  /** Gesetzt, wenn die Rechnung aus einer Serienrechnung entstanden ist. */
  recurringInvoiceId?: number | null;

  sections: InvoiceSection[];

  createdAt?: string;
  updatedAt?: string;
}

export interface RecurringInvoicePosition {
  id?: number;
  sortOrder: number;
  positionNumber: string;
  title: string;
  description?: string | null;
  unitPriceCents: number;
  quantity: number;
  unitLabel?: string | null;
}

/** Vorlage für eine wiederkehrende Rechnung; ein täglicher Job stellt sie zu. */
export interface RecurringInvoice {
  id?: number;
  clientId: number;
  clientName?: string | null;
  projectId?: number | null;
  projectName?: string | null;
  name: string;
  titleTemplate: string;
  sectionTitle: string;
  contactPerson?: string | null;
  recipientAddress?: string | null;
  introText?: string | null;
  notesHtml?: string | null;
  paymentTerms?: string | null;
  vatRatePercent: number;
  intervalMonths: number;
  dayOfMonth: number;
  dueDays: number;
  servicePeriodMonths: number;
  startDate: string;
  endDate?: string | null;
  nextRunDate: string;
  sendTo?: string | null;
  lastInvoiceId?: number | null;
  lastInvoiceNumber?: string | null;
  lastRunAt?: string | null;
  lastError?: string | null;
  lastErrorAt?: string | null;
  active: boolean;
  positions: RecurringInvoicePosition[];
}

export interface RevenueMonth {
  month: number;
  netCents: number;
  paidNetCents: number;
  /** Noch nicht gestellt, aber aus einer Serienrechnung zu erwarten. */
  forecastNetCents: number;
}

/** Eine Serienrechnung und das, was sie im Jahr noch beisteuert. */
export interface RevenueForecastSeries {
  id: number;
  name: string;
  clientName: string;
  netCentsPerRun: number;
  invoiceCount: number;
  netCents: number;
  months: number[];
}

/** Umsatz eines Jahres — netto, da die Umsatzsteuer durchlaufender Posten ist. */
export interface RevenueSummary {
  year: number;
  availableYears: number[];
  netCents: number;
  grossCents: number;
  paidNetCents: number;
  openNetCents: number;
  overdueNetCents: number;
  draftNetCents: number;
  invoiceCount: number;
  /** Hochrechnung: was die laufenden Serienrechnungen im Jahr noch erzeugen. */
  forecastNetCents: number;
  forecastInvoiceCount: number;
  /** Gestellt + Prognose — der Umsatz, mit dem für das Jahr zu rechnen ist. */
  expectedNetCents: number;
  forecastSeries: RevenueForecastSeries[];
  months: RevenueMonth[];
}

export interface InvoiceAuditEntry {
  id: number;
  invoiceId: number;
  invoiceNumber: string;
  action: string;
  actor: string;
  detail?: string | null;
  documentHash?: string | null;
  createdAt: string;
}

export interface SendInvoiceRequest {
  to: string;
  cc?: string | null;
  subject?: string | null;
  message?: string | null;
}

export interface CancelInvoiceRequest {
  reason: string;
}

export interface CreateInvoiceRequest {
  clientId: number;
  projectId?: number | null;
  offerId?: number | null;
  number: string;
  title: string;
  contactPerson?: string | null;
  recipientAddress?: string | null;
  invoiceDate: string;
  dueDate?: string | null;
  servicePeriodFrom?: string | null;
  servicePeriodTo?: string | null;
  introText?: string | null;
  notesHtml?: string | null;
  paymentTerms?: string | null;
  /** Verwendungszweck für die Überweisung; leer = die Rechnungsnummer. */
  paymentReference?: string | null;
  /** true = der Betrag war bei Ausstellung schon da (Vorkasse). */
  alreadyPaid?: boolean;
  vatRatePercent: number;
  notesInternal?: string | null;
  sections: InvoiceSection[];
}

export interface UpdateInvoiceRequest extends CreateInvoiceRequest {
  active: boolean;
}

export interface TransitionInvoiceStatusRequest {
  status: InvoiceStatus;
}

// ============================================================
// Banking — Kontoumsätze
// ============================================================

/** Wofür ein Konto geführt wird; die Rücklage zählt nicht zum verfügbaren Geld. */
export type AccountPurpose = 'OPERATING' | 'TAX_RESERVE' | 'SAVINGS';

/** Art einer Kategorie; TRANSFER ist weder Ertrag noch Aufwand. */
export type CategoryKind = 'INCOME' | 'EXPENSE' | 'TRANSFER' | 'TAX' | 'PRIVATE';

export type MatchSource = 'AUTO' | 'MANUAL';

export interface BankAccount {
  id: number;
  name: string;
  iban: string;
  bic?: string | null;
  bankName?: string | null;
  purpose: AccountPurpose;
  currency: string;
  openingBalanceCents: number;
  openingBalanceDate?: string | null;
  notes?: string | null;
  active: boolean;
  /** Anfangssaldo plus alle Umsätze. */
  balanceCents: number;
  transactionCount: number;
  lastTransactionDate?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface BankAccountRequest {
  name: string;
  iban: string;
  bic?: string | null;
  bankName?: string | null;
  purpose: AccountPurpose;
  currency: string;
  openingBalanceCents: number;
  openingBalanceDate?: string | null;
  notes?: string | null;
  active: boolean;
}

export interface TransactionCategory {
  id: number;
  name: string;
  kind: CategoryKind;
  accountCode?: string | null;
  /** Startwert für den Steuersatz eines Ausgangs; null = für die Vorsteuer nicht einschlägig. */
  defaultVatRatePercent?: number | null;
  /** Startwert für den Geschäftsbereich eines Umsatzes dieser Kategorie. */
  defaultBusinessArea?: string | null;
  sortOrder: number;
  active: boolean;
}

export interface TransactionCategoryRequest {
  name: string;
  kind: CategoryKind;
  accountCode?: string | null;
  defaultVatRatePercent?: number | null;
  /** Startwert für den Geschäftsbereich eines Umsatzes dieser Kategorie. */
  defaultBusinessArea?: string | null;
  sortOrder: number;
  active: boolean;
}

export interface BankTransaction {
  id: number;
  accountId: number;
  accountName?: string | null;
  bookingDate: string;
  /** Datum der Eingangsrechnung; leer = wie bookingDate. Maßgeblich für die Vorsteuer. */
  documentDate?: string | null;
  /** Das Datum, über das die Vorsteuer zählt — Rechnungsdatum, sonst Buchungsdatum. */
  vatDate?: string | null;
  counterparty: string;
  reference: string;
  /** Positiv = Eingang, negativ = Ausgang. */
  amountCents: number;
  currency: string;
  categoryId?: number | null;
  categoryName?: string | null;
  categoryKind?: CategoryKind | null;
  categoryAuto: boolean;
  /** Steuersatz des Ausgangs; bei Eingängen leer. */
  vatRatePercent?: number | null;
  vatAuto: boolean;
  /** Aufteilung auf mehrere Steuersätze; leer = vatRatePercent gilt für den ganzen Betrag. */
  vatSplits: VatSplit[];
  /** Errechnet: enthaltene Vorsteuer, positiv — aus der Aufteilung, sonst aus dem Satz. */
  inputVatCents: number;
  invoiceId?: number | null;
  invoiceNumber?: string | null;
  matchSource?: MatchSource | null;
  matchedAt?: string | null;
  /**
   * Welchem Vorhaben dieser Umsatz zugerechnet wird. Erst damit lässt sich einem Erlös die
   * Ausgabe gegenüberstellen. Ganz oder gar nicht — eine anteilige Aufteilung gibt es nicht.
   */
  businessArea?: string | null;
  /** true = aus der Kategorie übernommen und folgt ihr weiter; false = von Hand gesetzt. */
  businessAreaAuto: boolean;
  note?: string | null;
  /** Wie viele Dokumente (Belege) an diesem Umsatz hängen. */
  documentCount: number;
  createdAt?: string;
}

/** Ein Teilbetrag eines Umsatzes mit eigenem Steuersatz. */
export interface VatSplit {
  id?: number;
  sortOrder: number;
  grossCents: number;
  vatRatePercent: number;
  note?: string | null;
  /** Errechnet: die in grossCents enthaltene Steuer. */
  vatCents: number;
}

export interface VatSplitRequest {
  sortOrder: number;
  grossCents: number;
  vatRatePercent: number;
  note?: string | null;
}

export interface UpdateBankTransactionRequest {
  categoryId?: number | null;
  invoiceId?: number | null;
  note?: string | null;
  clearInvoice?: boolean;
  clearCategory?: boolean;
  markInvoicePaid?: boolean;
  /** Steuersatz des Ausgangs in Prozent. */
  vatRatePercent?: number | null;
  /** true = Steuersatz entfernen (kein Vorsteuerabzug). */
  clearVat?: boolean;
  /** Datum der Eingangsrechnung, falls es vom Buchungsdatum abweicht. */
  documentDate?: string | null;
  /** true = Rechnungsdatum entfernen (dann zählt wieder das Buchungsdatum). */
  clearDocumentDate?: boolean;
  /** Ersetzt die Steueraufteilung vollständig; eine leere Liste hebt sie auf. */
  vatSplits?: VatSplitRequest[];
  /** Vorhaben, dem dieser Umsatz zugerechnet wird. */
  businessArea?: string;
  /** true = Zuordnung aufheben (sonst lässt undefined das Feld unberührt). */
  clearBusinessArea?: boolean;
}

/** Ein Eingang, zu dem eine Rechnung passen könnte — bestätigt wird von Hand. */
export interface MatchSuggestion {
  transactionId: number;
  bookingDate: string;
  counterparty: string;
  amountCents: number;
  invoiceId: number;
  invoiceNumber: string;
  invoiceGrossCents: number;
  reason: string;
}

export interface BankImportResult {
  id?: number;
  accountId: number;
  accountName?: string | null;
  fileName: string;
  format: string;
  rowsTotal: number;
  rowsImported: number;
  rowsDuplicate: number;
  invoicesMatched: number;
  /** Belege, die der Import seinen Umsätzen zugeordnet hat. */
  receiptsMatched?: number;
  periodFrom?: string | null;
  periodTo?: string | null;
  importedBy: string;
  createdAt?: string;
  suggestions: MatchSuggestion[];
  warnings: string[];
}

export interface CategoryTotal {
  categoryId?: number | null;
  name: string;
  kind?: CategoryKind | null;
  amountCents: number;
  transactionCount: number;
}

export interface CashflowMonth {
  month: number;
  incomingCents: number;
  outgoingCents: number;
}

export interface BankingSummary {
  accounts: BankAccount[];
  year: number;
  availableYears: number[];
  /** Verfügbar: Salden der Geschäftskonten ohne die Rücklagen. */
  operatingBalanceCents: number;
  reserveBalanceCents: number;
  totalBalanceCents: number;
  incomingCents: number;
  outgoingCents: number;
  uncategorizedCount: number;
  unmatchedIncomingCount: number;
  categories: CategoryTotal[];
  months: CashflowMonth[];
  /** Vorsteuer je Quartal, aufgeschlüsselt nach Steuersatz. */
  vatQuarters: VatQuarter[];
  /** Ausgänge, denen noch ein Steuersatz fehlt. */
  missingVatCount: number;
  /** Ausgänge ohne Beleg (Umbuchungen, Steuern und Privates ausgenommen). */
  missingReceiptCount: number;
  /** Belege, die noch keinem Umsatz zugeordnet sind. */
  openReceiptCount: number;
}

/** Vorsteuer eines Quartals — aus dem Bruttobetrag herausgerechnet. */
export interface VatQuarter {
  quarter: number;
  grossExpenseCents: number;
  inputVatCents: number;
  transactionCount: number;
  /** Davon Buchungen, deren Rechnungsdatum in einem anderen Quartal liegt als die Zahlung. */
  shiftedCount: number;
  rates: VatRateTotal[];
  /** Umsatzsteuer aus den gestellten Rechnungen des Quartals. */
  salesVatCents: number;
  salesNetCents: number;
  invoiceCount: number;
  /** Umsatzsteuer minus Vorsteuer: positiv = ans Finanzamt, negativ = Erstattung. */
  payableCents: number;
}

export interface VatRateTotal {
  ratePercent: number;
  grossCents: number;
  netCents: number;
  inputVatCents: number;
  transactionCount: number;
}

/** Ergebnis eines zurückgenommenen Imports. */
export interface RevertImportResult {
  importId: number;
  accountId: number;
  accountName: string;
  fileName: string;
  deletedCount: number;
  /** Rechnungen, die an gelöschten Umsätzen hingen und nun ohne Zahlungsbeleg dastehen. */
  affectedInvoiceNumbers: string[];
  /** Umsätze, an denen von Hand gearbeitet wurde und die dabei verloren gehen. */
  editedCount: number;
}

/** Gestellte Rechnung als Auswahl für eine Zuordnung von Hand. */
export interface InvoiceMatchCandidate {
  id: number;
  number: string;
  status: InvoiceStatus;
  invoiceDate: string;
  dueDate?: string | null;
  clientName: string;
  netCents: number;
  grossCents: number;
}

/**
 * Was ein Vorhaben eingebracht und was es gekostet hat.
 *
 * Alles **netto**: die Umsatzsteuer ist auf beiden Seiten durchlaufender Posten.
 *
 * Zwei Zeitbegriffe, die man kennen muss: Erlöse zählen ab dem Rechnungsdatum, auch wenn noch
 * niemand gezahlt hat; Kosten zählen, sobald sie auf dem Kontoauszug stehen. Das Ergebnis ist
 * damit keine Kassenrechnung — `paidNetCents` sagt, wie viel vom Erlös wirklich da ist.
 */
export interface BusinessAreaResult {
  /** null = keinem Vorhaben zugeordnet. */
  area: string | null;
  revenueNetCents: number;
  paidNetCents: number;
  draftNetCents: number;
  invoiceCount: number;
  costNetCents: number;
  costGrossCents: number;
  transactionCount: number;
  /** Erlös minus Kosten. Negativ heißt: das Vorhaben hat bisher zugesetzt. */
  resultNetCents: number;
}

export interface BusinessAreaSummary {
  year: number;
  availableYears: number[];
  /** Ein Eintrag je Bereich, das Nicht-Zugeordnete am Ende. */
  areas: BusinessAreaResult[];
}

// ============================================================
// Dokumente (Belege, Briefe, Verträge, V21)
// ============================================================

/** Nur ein Beleg (RECEIPT) wird automatisch einem Kontoumsatz zugeordnet. */
export type DocumentKind = 'RECEIPT' | 'LETTER' | 'CONTRACT' | 'OTHER';
export type DocumentSource = 'SCAN' | 'UPLOAD' | 'SHARE';

/** Ein abgelegtes Dokument. Die Datei ist unveränderlich, die erfassten Angaben nicht. */
export interface DocumentRecord {
  id: number;
  clientRef?: string | null;
  kind: DocumentKind;
  fileName: string;
  contentType: string;
  fileSize: number;
  fileHash: string;
  source: DocumentSource;
  documentDate?: string | null;
  /** Betreff eines Briefs, Bezeichnung eines Vertrags. */
  title?: string | null;
  /** Händler, Absender oder Vertragspartner. */
  issuer?: string | null;
  /** Nur bei Belegen: Bruttobetrag, immer positiv. */
  amountCents?: number | null;
  currency: string;
  vatRatePercent?: number | null;
  note?: string | null;
  transactionId?: number | null;
  transactionBookingDate?: string | null;
  transactionCounterparty?: string | null;
  transactionAmountCents?: number | null;
  matchSource?: MatchSource | null;
  matchedAt?: string | null;
  uploadedBy: string;
  createdAt?: string;
}

export interface DocumentDetailsRequest {
  kind?: DocumentKind;
  documentDate?: string | null;
  title?: string | null;
  issuer?: string | null;
  amountCents?: number | null;
  currency?: string | null;
  vatRatePercent?: number | null;
  note?: string | null;
}

/** Ein Ausgang, zu dem ein Beleg passen könnte. */
export interface TransactionCandidate {
  transactionId: number;
  accountName: string;
  bookingDate: string;
  counterparty: string;
  reference: string;
  amountCents: number;
  dayOffset?: number | null;
  reason: string;
}

export type PrefillSource = 'FILE' | 'TRANSACTION';

export interface DocumentUploadResult {
  document: DocumentRecord;
  created: boolean;
  candidates: TransactionCandidate[];
  /** Was der Upload selbst ergänzt hat: aus der Textebene der PDF oder (Betrag) vom Umsatz. */
  prefilled?: Partial<Record<'amountCents' | 'documentDate' | 'vatRatePercent' | 'issuer', PrefillSource>>;
}
